import React from 'react';
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, Img, spring, interpolate } from 'remotion';
import { formatCurrency } from '../utils/formatters';
import { LOGO_URL } from '../common/constants';

const Watermark = () => {
  return (
    <div className="absolute top-12 left-12 z-50">
      <div className="bg-white/90 backdrop-blur-md p-3 rounded-2xl shadow-2xl border border-white/20 flex items-center gap-3">
        <Img src={LOGO_URL} style={{width: 60, height: 60, borderRadius: 12, objectFit: 'contain', padding: '4px'}} />
        <div>
          <p className="text-emerald-950 font-black text-sm leading-tight">ARBOLEDA</p>
          <p className="text-emerald-600 font-bold text-[8px] tracking-widest uppercase">Bienes Raíces</p>
        </div>
      </div>
    </div>
  );
};

const PropertySlide = ({ imageUrl, propertyType, price, currency, operation, city }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const opacity = spring({ frame, fps, config: { damping: 200 } });
  const scale = interpolate(frame, [0, 120], [1, 1.12]);

  return (
    <AbsoluteFill className="bg-black overflow-hidden">
      <Watermark />
      <Img 
        src={imageUrl} 
        style={{
          width: '100%', 
          height: '100%', 
          objectFit: 'cover',
          transform: `scale(${scale})`,
          opacity
        }} 
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-black/30" />
      
      <AbsoluteFill className="flex flex-col justify-end p-16 pb-40">
        <div style={{ opacity }}>
          <span className="bg-emerald-600 text-white px-8 py-3 rounded-2xl font-black text-3xl uppercase tracking-widest shadow-2xl">
            EN {operation}
          </span>
          <h1 className="text-white text-9xl font-black mt-10 leading-none drop-shadow-2xl">
            {propertyType}
          </h1>
          <p className="text-emerald-400 text-7xl font-black mt-6 drop-shadow-lg">
            {formatCurrency(price, currency)}
          </p>
          <p className="text-gray-300 text-4xl mt-6 flex items-center gap-4">
            📍 {city}, Honduras
          </p>
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

const ContactSlide = ({ agentName, agentPhone, agentEmail }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const progress = spring({ frame, fps, config: { stiffness: 60 } });

  return (
    <AbsoluteFill className="bg-emerald-950 flex flex-col items-center justify-center text-center p-20">
      <div style={{ opacity: progress, transform: `scale(${interpolate(progress, [0, 1], [0.8, 1])})` }}>
        <div className="bg-white p-6 rounded-[3.5rem] mb-12 inline-block shadow-2xl ring-8 ring-emerald-500/20 overflow-hidden w-72 h-72 flex items-center justify-center">
          <Img src={LOGO_URL} style={{width: '100%', height: '100%', objectFit: 'contain'}} />
        </div>
        <div className="space-y-8 mt-4">
          <h2 className="text-emerald-400 text-3xl font-black uppercase tracking-widest">Contáctanos</h2>
          <p className="text-white text-7xl font-black">{agentName}</p>
          <div className="bg-emerald-500 text-emerald-950 px-12 py-5 rounded-3xl text-6xl font-black inline-block shadow-xl">
            {agentPhone}
          </div>
          <p className="text-emerald-300 text-4xl opacity-80 mt-6">{agentEmail}</p>
        </div>
      </div>
    </AbsoluteFill>
  );
};

export const ReelComposition = ({ formData }) => {
  const { fps } = useVideoConfig();
  const images = formData.images.length > 0 ? formData.images : [{ src: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=1080&q=80' }];
  
  const slideDuration = 3.5 * fps;
  const endDuration = 4 * fps;

  return (
    <AbsoluteFill className="bg-black font-sans">
      {images.map((img, i) => (
        <Sequence key={i} from={i * slideDuration} durationInFrames={slideDuration}>
          <PropertySlide 
            imageUrl={img.src} 
            propertyType={formData.propertyType}
            price={formData.price}
            currency={formData.currency}
            operation={formData.operation}
            city={formData.city}
          />
        </Sequence>
      ))}
      <Sequence from={images.length * slideDuration} durationInFrames={endDuration}>
        <ContactSlide 
          agentName={formData.agentName}
          agentPhone={formData.agentPhone}
          agentEmail={formData.agentEmail}
        />
      </Sequence>
    </AbsoluteFill>
  );
};